import { useState, useCallback } from 'react';
import { Eye, Check, Target, BookOpen, ListChecks, FlaskConical } from 'lucide-react';
import InfoBox from '../InfoBox';
import { PrismSpectrometer } from '@/components/lab';

const steps = [
  { id: 1, title: 'Measure Red Line', short: 'Find and record the 656.3 nm line' },
  { id: 2, title: 'Measure Blue Line', short: 'Find and record the 486.1 nm line' },
  { id: 3, title: 'Measure Purple Line', short: 'Find and record the 434.0 nm line' },
  { id: 4, title: 'Measure Purple (weak)', short: 'Find and record the 410.2 nm line' }
];

const stepInfoContent: Record<number, { title: string; content: string }> = {
  1: {
    title: 'Red Line (656.3 nm)',
    content: 'The red hydrogen line is the most prominent visible emission line, resulting from the electron transition from n=3 to n=2 (Balmer series). Use the Vernier scale to read the angle. The Vernier scale has an arrow and 10 additional lines - each represents a tenth of a degree. For all visible transitions of hydrogen, n₁ = 2.'
  },
  2: {
    title: 'Blue Line (486.1 nm)',
    content: 'The blue hydrogen line results from the n=4 to n=2 transition. With higher energy than the red line, it has a shorter wavelength. Move the crosshairs to locate the blue line and record the angle using the Vernier scale.'
  },
  3: {
    title: 'Purple Line (434.0 nm)',
    content: 'The purple hydrogen line results from the n=5 to n=2 transition. As we move to higher initial states, the lines become closer together and approach the series limit at 364.6 nm (the Balmer series convergence).'
  },
  4: {
    title: 'Purple (weak) Line (410.2 nm)',
    content: 'The purple (weak) hydrogen line results from the n=6 to n=2 transition. This line may be difficult to see and is near the visibility limit for many observers. Record the angle using the Vernier scale.'
  }
};

interface Part2MeasurementProps {
  onStepComplete?: (stepId: string) => void;
  currentStep?: number;
  onRecordLine?: (line: RecordedLine) => void;
}

interface RecordedLine {
  name: string;
  transition: string;
  angle: number;
  wavelength: number;
  color: string;
}

// Hydrogen Balmer series lines
const BALMER_LINES = [
  { name: 'Red', transition: '3→2', wavelength: 656.3, color: '#FF0000', targetAngle: 45 },
  { name: 'Blue', transition: '4→2', wavelength: 486.1, color: '#0000FF', targetAngle: 65 },
  { name: 'Purple', transition: '5→2', wavelength: 434.0, color: '#8A2BE2', targetAngle: 78 },
  { name: 'Purple (weak)', transition: '6→2', wavelength: 410.2, color: '#9400D3', targetAngle: 88 }
];

export default function Part2Measurement({ onStepComplete, currentStep = 1, onRecordLine }: Part2MeasurementProps) {
  const [spectrometerAngle, setSpectrometerAngle] = useState(90);
  const [currentLineIndex, setCurrentLineIndex] = useState(0);
  const [recordedLines, setRecordedLines] = useState<RecordedLine[]>([]);
  const [foundLine, setFoundLine] = useState(false);
  const [showInfo, setShowInfo] = useState<number | null>(null);

  const stepDone = [
    recordedLines.length >= 1,
    recordedLines.length >= 2,
    recordedLines.length >= 3,
    recordedLines.length >= 4
  ];
  const completedCount = stepDone.filter(Boolean).length;
  const activeStep = completedCount < steps.length ? completedCount + 1 : steps.length;

  const currentTarget = BALMER_LINES[currentLineIndex];
  const isNearTarget = Math.abs(spectrometerAngle - currentTarget.targetAngle) < 5;

  const handleAngleChange = useCallback((angle: number) => {
    setSpectrometerAngle(angle);
    // Check if line is found
    if (Math.abs(angle - currentTarget.targetAngle) < 3) {
      setFoundLine(true);
    } else {
      setFoundLine(false);
    }
  }, [currentTarget]);

  const handleRecordMeasurement = useCallback(() => {
    if (foundLine) {
      const newLine: RecordedLine = {
        name: currentTarget.name,
        transition: currentTarget.transition,
        angle: spectrometerAngle,
        wavelength: currentTarget.wavelength,
        color: currentTarget.color
      };

      setRecordedLines(prev => [...prev, newLine]);
      if (onRecordLine) onRecordLine(newLine);

      // Complete step
      const stepNum = currentLineIndex * 2 + 2;
      if (onStepComplete) onStepComplete(`measurement-${stepNum}`);

      // Move to next line
      if (currentLineIndex < BALMER_LINES.length - 1) {
        setCurrentLineIndex(prev => prev + 1);
        setFoundLine(false);
      }
    }
  }, [foundLine, currentTarget, spectrometerAngle, currentLineIndex, onStepComplete, onRecordLine]);

  const handleFindLine = useCallback(() => {
    // Just mark that user is looking for the line
    const stepNum = currentLineIndex * 2 + 1;
    if (onStepComplete) onStepComplete(`measurement-${stepNum}`);
  }, [currentLineIndex, onStepComplete]);

  const allLinesRecorded = recordedLines.length >= BALMER_LINES.length;

  return (
    <div className="exp1-part-layout">
      {/* Steps Panel */}
      <div className="exp1-steps-panel">
        <div className="exp1-steps-header">
          <ListChecks size={16} />
          <h3>Part II: Measurement</h3>
        </div>
        <div className="exp1-steps-list">
          {steps.map(s => {
            const done = stepDone[s.id - 1];
            const active = s.id === activeStep;
            return (
              <div key={s.id} className={`exp1-step-item ${active && !done ? 'active' : ''}`}>
                <div className={`exp1-step-number ${done ? 'done' : active ? 'current' : ''}`}>
                  {done ? <Check size={12} /> : s.id}
                </div>
                <div className="exp1-step-content">
                  <span className={`exp1-step-title ${active ? 'highlight' : ''}`}>{s.title}</span>
                  {active && !done && <span className="exp1-step-hint">{s.short}</span>}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Lab Bench */}
      <div className="exp1-lab-bench">
        <div className="exp1-bench-header">
          <div className="exp1-bench-title"><FlaskConical size={16} /><h3>Lab Bench</h3></div>
          <span className="exp1-bench-progress">{completedCount}/{steps.length} Complete</span>
        </div>
        <div className="exp1-bench-area">
      <div className="measurement-lab">
        {/* Spectrometer */}
        <div className="spectrometer-area">
          <PrismSpectrometer
            angle={spectrometerAngle}
            viewingSpectrum={true}
            activeGas="hydrogen"
            highlightedLine={foundLine ? { wavelength: currentTarget.wavelength, color: currentTarget.color } : undefined}
            onAngleChange={handleAngleChange}
          />
        </div>

        {/* Current Target */}
        {!allLinesRecorded && (
          <div className="target-panel">
            <div className="target-header">
              <div className="target-header-left">
                <Target size={16} />
                <span>Find: {currentTarget.name} Line ({currentTarget.transition})</span>
              </div>
              {stepInfoContent[currentLineIndex + 1] && (
                <button
                  className="step-info-btn"
                  onClick={(e) => { e.stopPropagation(); setShowInfo(currentLineIndex + 1); }}
                >
                  ?
                </button>
              )}
            </div>
            <div className="target-info">
              <div className="target-color" style={{ background: currentTarget.color }} />
              <span className="target-wavelength">Expected: ~{currentTarget.wavelength} nm</span>
            </div>
            <p className="target-hint">
              Rotate the spectrometer telescope to locate the {currentTarget.color === '#FF0000' ? 'red' :
                currentTarget.color === '#00FFFF' ? 'cyan (blue-green)' :
                currentTarget.color === '#8A2BE2' ? 'blue-violet' : 'violet'} line.
            </p>

            {/* Alignment indicator */}
            <div className="alignment-indicator">
              <div className="alignment-bar">
                <div
                  className={`alignment-marker ${isNearTarget ? 'near' : ''} ${foundLine ? 'aligned' : ''}`}
                  style={{ left: `${Math.max(0, Math.min(100, (spectrometerAngle / 180) * 100))}%` }}
                />
                <div
                  className="target-marker"
                  style={{ left: `${(currentTarget.targetAngle / 180) * 100}%` }}
                />
              </div>
              <span className="alignment-status">
                {foundLine ? 'Line found! Ready to record.' :
                  isNearTarget ? 'Getting close...' : 'Searching...'}
              </span>
            </div>

            <div className="action-buttons">
              {!foundLine && (
                <button className="action-btn secondary" onClick={handleFindLine}>
                  <Eye size={16} />
                  Looking for Line
                </button>
              )}
              {foundLine && (
                <button className="action-btn success" onClick={handleRecordMeasurement}>
                  <Check size={16} />
                  Record Measurement
                </button>
              )}
            </div>
          </div>
        )}

        {/* Data Table */}
        <div className="data-table">
          <h4>Recorded Measurements</h4>
          <table>
            <thead>
              <tr>
                <th>Line</th>
                <th>Transition</th>
                <th>Angle</th>
                <th>Wavelength</th>
                <th>Color</th>
              </tr>
            </thead>
            <tbody>
              {recordedLines.map((line, i) => (
                <tr key={i}>
                  <td>{line.name}</td>
                  <td>n={line.transition}</td>
                  <td>{line.angle.toFixed(1)}°</td>
                  <td>{line.wavelength} nm</td>
                  <td>
                    <div className="color-swatch" style={{ background: line.color }} />
                  </td>
                </tr>
              ))}
              {recordedLines.length < BALMER_LINES.length && (
                <tr className="pending">
                  <td>{currentTarget.name}</td>
                  <td>n={currentTarget.transition}</td>
                  <td>-</td>
                  <td>-</td>
                  <td>
                    <div className="color-swatch" style={{ background: currentTarget.color, opacity: 0.3 }} />
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Energy Level Diagram */}
        <div className="energy-diagram">
          <h4>Hydrogen Energy Levels (Balmer Series)</h4>
          <svg viewBox="0 0 300 180" width={300} height={180}>
            {/* Energy levels */}
            {[2, 3, 4, 5, 6].map((n, i) => {
              const y = 160 - (1 - 1/(n*n)) * 140;
              return (
                <g key={n}>
                  <line x1="50" y1={y} x2="200" y2={y} stroke="#666" strokeWidth="2" />
                  <text x="210" y={y + 4} fontSize="10" fill="#888">n = {n}</text>
                  <text x="30" y={y + 4} fontSize="8" fill="#666" textAnchor="end">
                    {(-13.6/(n*n)).toFixed(2)} eV
                  </text>
                </g>
              );
            })}

            {/* Transitions */}
            {recordedLines.map((line, i) => {
              const nUpper = parseInt(line.transition.split('→')[0]);
              const nLower = 2;
              const yUpper = 160 - (1 - 1/(nUpper*nUpper)) * 140;
              const yLower = 160 - (1 - 1/(nLower*nLower)) * 140;
              const x = 80 + i * 35;

              return (
                <g key={i}>
                  <line
                    x1={x}
                    y1={yUpper}
                    x2={x}
                    y2={yLower}
                    stroke={line.color}
                    strokeWidth="3"
                    strokeLinecap="round"
                  />
                  <polygon
                    points={`${x-4},${yLower-8} ${x+4},${yLower-8} ${x},${yLower}`}
                    fill={line.color}
                  />
                </g>
              );
            })}

            {/* Labels */}
            <text x="125" y="15" fontSize="9" fill="#666" textAnchor="middle">
              Visible emissions (n → 2)
            </text>
          </svg>
        </div>

        {/* Completion */}
        {allLinesRecorded && (
          <div className="completion-panel">
            <Check size={24} />
            <div>
              <h4>All Balmer Lines Recorded!</h4>
              <p>You have successfully measured all four visible hydrogen emission lines.</p>
              <p>Proceed to Part III to calculate the Rydberg constant.</p>
            </div>
          </div>
        )}

        <style>{`
          .exp1-part-layout { display: flex; gap: 16px; flex: 1; width: 100%; min-height: 0; padding: 16px; }
          .exp1-steps-panel { width: 240px; background: var(--bg-surface); border-radius: 12px; border: 1px solid var(--border); display: flex; flex-direction: column; overflow: hidden; }
          .exp1-steps-header { background: linear-gradient(135deg, var(--primary), var(--primary-dark)); padding: 14px 16px; display: flex; align-items: center; gap: 10px; color: white; }
          .exp1-steps-header h3 { font-size: 14px; font-weight: 600; margin: 0; }
          .exp1-steps-list { flex: 1; overflow-y: auto; padding: 12px; }
          .exp1-step-item { display: flex; align-items: flex-start; gap: 12px; padding: 8px 10px; border-radius: 8px; margin-bottom: 2px; }
          .exp1-step-item.active { background: rgba(255, 90, 54, 0.15); }
          .exp1-step-number { width: 24px; height: 24px; border-radius: 50%; display: flex; align-items: center; justify-content: center; font-size: 11px; font-weight: 600; flex-shrink: 0; background: var(--border); color: var(--text-muted); }
          .exp1-step-number.done { background: var(--secondary); color: white; }
          .exp1-step-number.current { background: var(--primary); color: white; }
          .exp1-step-content { flex: 1; min-width: 0; }
          .exp1-step-title { font-size: 12px; font-weight: 600; display: block; }
          .exp1-step-title.highlight { color: var(--primary-light); }
          .exp1-step-hint { font-size: 11px; color: var(--text-secondary); margin-top: 4px; display: block; }
          .exp1-lab-bench { flex: 1; background: var(--bg-surface); border-radius: 12px; border: 1px solid var(--border); display: flex; flex-direction: column; overflow: auto; }
          .exp1-bench-header { background: linear-gradient(135deg, var(--secondary), var(--secondary-dark)); padding: 12px 16px; display: flex; align-items: center; justify-content: space-between; color: white; }
          .exp1-bench-title { display: flex; align-items: center; gap: 10px; }
          .exp1-bench-title h3 { font-size: 14px; font-weight: 600; margin: 0; }
          .exp1-bench-progress { font-size: 12px; opacity: 0.9; }
          .exp1-bench-area { flex: 1; padding: 20px; overflow-y: auto; }

          .measurement-lab {
            display: flex;
            flex-direction: column;
            align-items: center;
            gap: 24px;
            width: 100%;
            max-width: 700px;
          }

          .spectrometer-area {
            background: var(--bg-elevated);
            padding: 20px;
            border-radius: 12px;
          }

          .target-panel {
            width: 100%;
            background: var(--bg-elevated);
            padding: 16px 20px;
            border-radius: 12px;
            border: 2px solid var(--primary);
          }

          .target-header {
            display: flex;
            align-items: center;
            justify-content: space-between;
            font-size: 14px;
            font-weight: 600;
            color: var(--primary);
            margin-bottom: 12px;
          }

          .target-header-left {
            display: flex;
            align-items: center;
            gap: 8px;
          }

          .target-info {
            display: flex;
            align-items: center;
            gap: 12px;
            margin-bottom: 8px;
          }

          .target-color {
            width: 24px;
            height: 24px;
            border-radius: 50%;
            border: 2px solid rgba(255,255,255,0.3);
          }

          .target-wavelength {
            font-size: 13px;
            color: var(--text-secondary);
          }

          .target-hint {
            font-size: 12px;
            color: var(--text-muted);
            margin: 0 0 16px 0;
          }

          .alignment-indicator {
            margin-bottom: 16px;
          }

          .alignment-bar {
            position: relative;
            height: 8px;
            background: var(--bg-dark);
            border-radius: 4px;
            margin-bottom: 8px;
          }

          .alignment-marker {
            position: absolute;
            top: -4px;
            width: 16px;
            height: 16px;
            background: #FF5A36;
            border-radius: 50%;
            transform: translateX(-50%);
            transition: left 0.1s, background 0.2s;
          }

          .alignment-marker.near {
            background: #FFC107;
          }

          .alignment-marker.aligned {
            background: #00A86B;
          }

          .target-marker {
            position: absolute;
            top: 0;
            width: 4px;
            height: 8px;
            background: #666;
            transform: translateX(-50%);
          }

          .alignment-status {
            font-size: 11px;
            color: var(--text-muted);
          }

          .action-buttons {
            display: flex;
            gap: 12px;
          }

          .action-btn {
            display: flex;
            align-items: center;
            gap: 8px;
            padding: 10px 16px;
            background: var(--primary);
            color: white;
            border: none;
            border-radius: 8px;
            font-size: 13px;
            font-weight: 500;
            cursor: pointer;
            transition: all 0.2s;
          }

          .action-btn.secondary {
            background: var(--bg-dark);
            border: 1px solid var(--border);
            color: var(--text-secondary);
          }

          .action-btn.success {
            background: #00A86B;
          }

          .data-table {
            width: 100%;
            background: var(--bg-elevated);
            padding: 16px;
            border-radius: 12px;
          }

          .data-table h4 {
            margin: 0 0 12px 0;
            font-size: 13px;
            color: var(--text-secondary);
          }

          table {
            width: 100%;
            border-collapse: collapse;
            font-size: 12px;
          }

          th, td {
            padding: 8px 12px;
            text-align: center;
            border: 1px solid var(--border);
          }

          th {
            background: var(--bg-dark);
            color: var(--text-secondary);
            font-weight: 600;
          }

          td {
            color: var(--text-primary);
          }

          tr.pending td {
            color: var(--text-muted);
            font-style: italic;
          }

          .color-swatch {
            width: 20px;
            height: 20px;
            border-radius: 4px;
            margin: 0 auto;
          }

          .energy-diagram {
            width: 100%;
            background: var(--bg-elevated);
            padding: 16px;
            border-radius: 12px;
            text-align: center;
          }

          .energy-diagram h4 {
            margin: 0 0 12px 0;
            font-size: 13px;
            color: var(--text-secondary);
          }

          .completion-panel {
            display: flex;
            gap: 16px;
            padding: 20px;
            background: rgba(0, 168, 107, 0.1);
            border: 1px solid #00A86B;
            border-radius: 12px;
            color: #00A86B;
            width: 100%;
          }

          .completion-panel h4 {
            margin: 0 0 8px 0;
          }

          .completion-panel p {
            margin: 0 0 4px 0;
            font-size: 13px;
            color: var(--text-secondary);
          }
        .step-info-btn {
          width: 18px;
          height: 18px;
          border-radius: 50%;
          background: var(--accent);
          color: var(--bg-dark);
          font-size: 11px;
          font-weight: 700;
          border: none;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
          transition: transform 0.15s;
        }
        .step-info-btn:hover {
          transform: scale(1.1);
        }
        `}</style>

        {showInfo && stepInfoContent[showInfo] && (
          <InfoBox
            step={showInfo}
            experimentId="exp1"
            stepInfo={stepInfoContent[showInfo]}
            onClose={() => setShowInfo(null)}
          />
        )}
      </div>
        </div>
      </div>
    </div>
  );
}
